import { Badge, Button, Checkbox, IconButton, List, Popover, Tooltip } from "@mui/material";
import ExportModal from "components/ExportModal";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  deselectExportType,
  selectCaseClaimantName,
  selectDeselectedExportTypes,
  selectExportType,
  selectFilteredCaseExports,
  selectUniqueExportTypes,
} from "redux/slices/case/";
import { useExportFunctions } from "helpers/hooks/useExportFunctions";
import ExportChronologyModal from "routes/lenses/lens/case/ExportChronologyModal";
import { HeaderCard, HeaderCardTitle } from "components/HeaderCard";
import SBRExportsCenter from "./ExportCenter";
import { Add, Event, FilterList, Work } from "@mui/icons-material";
import { selectChronologyConfig } from "redux/slices/clientLens";
import { pluralize } from "helpers/coll";
import { usePopover } from "helpers/hooks/usePopover";

export default function SBRCaseExports({ caseId }: { caseId: number }) {
  const [exportCaseProfileModalOpen, setExportCaseProfileModalOpen] = useState(false);
  const [exportCaseChronologyModalOpen, setExportCaseChronologyModalOpen] = useState(false);
  const claimantName = useAppSelector(selectCaseClaimantName);
  const { doChronologyExport } = useExportFunctions();
  const filteredExports = useAppSelector(selectFilteredCaseExports);
  const deselectedExportTypes = useAppSelector(selectDeselectedExportTypes);
  const visibleExportTypes = filteredExports.map((e) => e.exportType);
  const chronologyConfig = useAppSelector(selectChronologyConfig);
  const uniqueExportTypes = useAppSelector(selectUniqueExportTypes);
  const dispatch = useAppDispatch();
  const filterListPopover = usePopover();
  const generateExportPopover = usePopover();

  const exportBody = (
    <div className="flex flex-col">
      <p className="font-bold text-xl">Profile Export Settings</p>
      <p className="font-semibold text-xs">{claimantName}</p>
    </div>
  );

  return (
    <HeaderCard
      title={
        <HeaderCardTitle className="flex items-center">
          <p className="text-sm font-semibold" data-pendo="export-center-title text-sm">
            Exports {filteredExports.length ? `(${filteredExports.length})` : ""}
          </p>
          <div className="grow" />
          <Tooltip
            arrow
            title={`${deselectedExportTypes.length} ${pluralize(deselectedExportTypes.length, "export")} filtered`}>
            <IconButton
              data-pendo="export-filter"
              onClick={(evt) => filterListPopover.onPopoverOpen(evt.currentTarget)}
              size="small">
              <Badge badgeContent={deselectedExportTypes.length} color="primary">
                <FilterList />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Create Export" arrow>
            <IconButton
              data-pendo="case-home-generate-export"
              onClick={(evt) => generateExportPopover.onPopoverOpen(evt.currentTarget)}
              size="small">
              <Add />
            </IconButton>
          </Tooltip>
          <Popover
            open={filterListPopover.isOpen}
            anchorEl={filterListPopover.anchorEl}
            onClose={filterListPopover.onPopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}>
            <List sx={{ padding: 2 }}>
              {uniqueExportTypes.map((exportType) => (
                <div className="flex" key={exportType}>
                  <Checkbox
                    checked={visibleExportTypes.indexOf(exportType) > -1}
                    onChange={(_evt, checked) => {
                      if (!checked) {
                        dispatch(deselectExportType(exportType));
                      } else {
                        dispatch(selectExportType(exportType));
                      }
                    }}
                  />
                  <p className="pt-6 text-sm">{exportType}</p>
                </div>
              ))}
            </List>
          </Popover>

          <Popover
            open={generateExportPopover.isOpen}
            anchorEl={generateExportPopover.anchorEl}
            onClose={generateExportPopover.onPopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}>
            <div className="flex flex-col items-start p-2">
              <Tooltip title="PDF of Case Profile and pinned references" arrow placement="left">
                <Button
                  fullWidth
                  id="case_form_export"
                  color="default"
                  size="large"
                  onClick={() => {
                    generateExportPopover.onPopoverClose();
                    setExportCaseProfileModalOpen(true);
                  }}
                  startIcon={<Work />}>
                  Case Profile Export
                </Button>
              </Tooltip>
              {chronologyConfig && (
                <Tooltip title="Excel of Case Profile in chronological order" arrow placement="left">
                  <Button
                    fullWidth
                    id="case_chronology_export"
                    color="default"
                    size="large"
                    onClick={() => {
                      generateExportPopover.onPopoverClose();
                      setExportCaseChronologyModalOpen(true);
                    }}
                    startIcon={<Event />}>
                    Chronology Export
                  </Button>
                </Tooltip>
              )}
            </div>
          </Popover>
        </HeaderCardTitle>
      }>
      <SBRExportsCenter exports={filteredExports} caseId={caseId} />
      {exportCaseProfileModalOpen && (
        <ExportModal
          body={exportBody}
          open={exportCaseProfileModalOpen}
          setOpen={setExportCaseProfileModalOpen}
          caseId={caseId}
        />
      )}

      <ExportChronologyModal
        open={exportCaseChronologyModalOpen}
        close={() => setExportCaseChronologyModalOpen(false)}
        caseId={caseId}
        doExport={doChronologyExport}
        claimant={claimantName}
      />
    </HeaderCard>
  );
}
